import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import { Popover } from 'antd';
import React, { FC, useCallback, useEffect } from 'react';
import { SpaceTabSkelton } from 'api/space';
import { Task } from 'api/tasks';
import { SquadData } from 'components/molecules/squad-picker/use-squad-options';
import { useCKMentionEvents } from 'hooks/use-ck-mention-events';
import {
  MentionItem,
  MentionType,
  Suggestion,
} from 'vendor/ckeditor/plugins/mention/types';
import { MentionPopupContent } from './mention-popup-content';
import { setEditor, useMentions } from './mention-store';

export type MentionSuggestion = Partial<MentionItem>;

type MentionTabData<T> = {
  isLoading?: boolean;
  items: T;
  title?: string;
};

export interface MentionData {
  members?: MentionTabData<SquadData>;
  spaceTabs?: MentionTabData<SpaceTabSkelton[]>;
  tasks?: MentionTabData<Task[]>;
}

export type OnUserMention = (
  selected: MentionSuggestion,
  callback: (selected: MentionSuggestion) => void,
) => void;

export type OnStatusUpdateUserMention = (
  selected: Suggestion,
  callback: (selected: Suggestion) => void,
  { hideAddCollabAlert }: { hideAddCollabAlert: boolean },
) => void;

interface Props {
  visible: boolean;
  editor: Editor;
  overlayClassName?: string;
  onTabChange: (tab: string) => void;
  attachTo?: HTMLElement | null;
  activeTab?: MentionType;
  onSelect: (args) => void;
  query: string;
  data?: MentionData;
  placement?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom'
    | undefined;
}

export const MentionPopup: FC<Props> = ({
  visible,
  overlayClassName,
  attachTo,
  activeTab,
  onTabChange,
  onSelect,
  query,
  data,
  editor,
  placement,
  children,
}) => {
  const [contextState, contextDispatch] = useMentions();

  useEffect(() => {
    if (editor && !contextState.editor) {
      contextDispatch(setEditor(editor));
    }
  }, [editor, contextState, contextDispatch]);

  const handleCKMentionEvents = useCallback(
    (e) => {
      if (e.name === 'mentionPopup:executeSelected') {
        const { activeSuggestion } = contextState;

        const item = {
          id: `@${activeSuggestion?.name}`,
          name: activeSuggestion?.name,
          objectId: activeSuggestion?.objectId,
          projectId: activeSuggestion?.projectId,
          spaceId: activeSuggestion?.spaceId,
          subType: activeSuggestion?.subType,
          photoUrl: activeSuggestion?.photoUrl,
          type: activeSuggestion?.type,
        };

        if (activeSuggestion) {
          onSelect(item);
        }
      }
    },
    // Don't update on onSelect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextState],
  );

  useCKMentionEvents(handleCKMentionEvents, editor);

  return (
    <Popover
      overlayClassName={overlayClassName}
      getPopupContainer={attachTo ? () => attachTo as HTMLElement : undefined}
      visible={visible}
      placement={placement}
      arrowContent={false}
      content={
        <MentionPopupContent
          onTabChange={onTabChange}
          onSelect={onSelect}
          query={query}
          activeTab={activeTab}
          data={data}
        />
      }
    >
      {children}
    </Popover>
  );
};

export { MentionType };
